import { FC, useRef, useMemo, useCallback, lazy, Suspense, memo, useState } from 'react';
import { Box, CircularProgress, Paper } from '@mui/material';
import Draggable from 'react-draggable';
import useDraggableChat from '../../hooks/useDraggableChat';
import styles from './ChatDialog.module.scss';
import MinimizedChatIcon from '../MinimizedIcon/MinimizedChatIcon';
const ChatHeader = lazy(() => import('../ChatHeader/ChatHeader'));
const ChatInput = lazy(() => import('../ChatInput/ChatInput'));
const ChatMessages = lazy(() => import('../ChatMessages/ChatMessages'));
const ChatWelcomeScreen = lazy(() => import('../ChatWelcomeScreen/ChatWelcomeScreen'));
const ChatPastSessions = lazy(() => import('../ChatPastSessions/ChatPastSessions'));

interface ChatDialogProps {
  open: boolean;
  onClose: () => void;
}

const ChatDialog: FC<ChatDialogProps> = ({ open, onClose }) => {
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null); 

  const { view, currentConversation, isMinimized, setIsMinimized, isInitializing, dragPosition, setDragPosition, } = useDraggableChat();

  const messages = useMemo(() => currentConversation?.messages || [], [currentConversation]);
  const isLoading = useMemo(() => isInitializing || currentConversation?.loading, [isInitializing, currentConversation?.loading]);

  const showWelcome = useMemo(() => {
    return !isLoading && view === 'chat' && messages.length === 0;
  }, [view, messages.length, isLoading]);

  const handleSuggestionClick = (suggestion: string) => {
    setInputValue(suggestion);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        const end = suggestion.length;
        inputRef.current.setSelectionRange(end, end);
      }
    }, 0);
  };

  const handleMaximize = useCallback(() => {
    setIsMinimized(false);
  }, [setIsMinimized]);

  const closeMinimizedIcon = () => {
    setIsMinimized(false);
    onClose();
  };

  const handleStop = (e: any, data: any) => {
      setDragPosition({x: data.x, y: data.y});
  };

  if (!open) return null;
  if (isMinimized) return <MinimizedChatIcon onClick={handleMaximize} onClose={closeMinimizedIcon} />;

  const renderContent = () => {
    if (view === 'pastChats') {
        return <ChatPastSessions />;
    }

    if (isInitializing) {
      return (
          <Box
              sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
              }}
          >
              <CircularProgress color='inherit' />
          </Box>
      );
    }

    if (showWelcome) {
      return (
        <>
          <ChatWelcomeScreen />
          <div className="non-draggable-input">
            <ChatInput ref={inputRef} inputValue={inputValue} setInputValue={setInputValue} />
          </div>
        </>
      );
    }
    
    return (
      <>
        <ChatMessages isLoading={isLoading || false} onSuggestionClick={handleSuggestionClick} />
        <div className="non-draggable-input">
          <ChatInput ref={inputRef} inputValue={inputValue} setInputValue={setInputValue} />
        </div>
      </>
    );
  };

  return (
    <Draggable
      nodeRef={nodeRef}
      position={dragPosition}
      onStop={handleStop}
      cancel=".non-draggable-input"
      bounds="body"
    >
      <Box ref={nodeRef} className={styles.chatDialog} sx={{ bottom: 20, right: 20 }}>
        <Paper className={styles.paper}>
          <Suspense>
            <ChatHeader onClose={onClose} />
            <Box className={styles.content} sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              {renderContent()}
            </Box>
          </Suspense>
        </Paper>
      </Box>
    </Draggable>
  );
};

export default memo(ChatDialog);